import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthImage } from '@/components/auth-image';
import { Bell, BellRing, Minus, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { use_watch_variant } from '@/features/stock/hooks';
import { use_cart } from '@/features/commerce/cart';
import { FavouriteProductButton } from '@/components/favourite-product-button';
import type { ShopProduct } from '@/features/shop/types';

/**
 * A single product tile in the product-first Shop. Shows the product, a variant chip selector
 * (when the product has more than one pack/size), the selected variant's price + any discount,
 * and an add-to-cart control. The selling vendor is never shown.
 */
export function ShopProductCard({
  product,
  vertical,
}: {
  product: ShopProduct;
  /**
   * Which catalogue this card came from.
   *
   * Passed in rather than read off the product: the shop payload deliberately carries no
   * vertical (each tab queries one), and the watch endpoint needs it. Taking it from the
   * page that already knows beats widening the payload for one button.
   */
  vertical: 'FOOD' | 'PHARMACY';
}) {
  const watch_m = use_watch_variant();
  /** Local, per size: the button has to change the moment they tap it. */
  const [watched, set_watched] = useState<Record<string, boolean>>({});
  const [variant_id, set_variant_id] = useState(product.variants[0]?.id ?? '');
  const cart = use_cart();
  const qty = cart.qty_of(variant_id);

  const selected = product.variants.find((v) => v.id === variant_id) ?? product.variants[0];
  if (!selected) return null;

  /*
   * Buyable, not merely in stock.
   *
   * `is_in_stock` answers the count alone, so a size sitting on the shelf under a listing the
   * vendor switched OFF reports true here while the cart and checkout both refuse it. That is
   * also the commonest reason a product is unavailable, so gating "Notify me" on the count
   * hid the button in exactly the case it exists for.
   *
   * Falls back to `is_in_stock` when the server has not sent the newer flag, so an older API
   * degrades to the previous behaviour rather than treating everything as unbuyable.
   */
  const can_buy = selected.is_purchasable ?? selected.is_in_stock !== false;
  const blocked_reason = selected.unavailable_reason ?? (can_buy ? null : 'OUT_OF_STOCK');
  /** Review and suspension are not waiting on a restock, so a watch is refused for them. */
  const watchable = blocked_reason === 'OUT_OF_STOCK' || blocked_reason === 'LISTING_PAUSED';

  // The variant id alone tells the server which catalog and which vendor this came
  // from, so nothing about price, vertical or vendor is sent from here — the client
  // can't quote a price the server didn't set.
  const add = () => cart.add(selected.id);

  return (
    <Card className="flex flex-col overflow-hidden" data-testid={`shop-product-${product.id}`}>
      {/*
        * The photo opens the product page, not a lightbox.
        *
        * It is the biggest target on the card and the one people reach for, and the page it
        * leads to has a proper gallery with zoom — a lightbox here would be a second, worse
        * viewer competing with it. ImageCarousel is left alone for its other callers, which
        * have no detail page to go to.
        */}
      <div className="relative">
        {/*
          Overlaid on the photo rather than placed in the body: the heart has to be reachable
          without opening the product, and the photo is the only part of the card with room.
          Its own click handler stops the event so saving never navigates.
        */}
        <FavouriteProductButton
          vertical={vertical.toLowerCase() as 'food' | 'pharmacy'}
          product_id={product.id}
          product_name={product.name}
          size="sm"
          className="absolute right-2 top-2 z-10"
        />
      <Link
        to={`/app/shop/p/${product.id}?vertical=${vertical.toLowerCase()}`}
        className="block aspect-square w-full bg-secondary/30"
        aria-label={`View ${product.name}`}
        data-testid={`shop-open-image-${product.id}`}
      >
        {/* The API picks between the vendor's photo and the curated master's; the card just
            draws what it is given, or a paw when there genuinely is nothing. */}
        {product.primary_image ? (
          <AuthImage
            path={product.primary_image.thumb_path}
            alt={product.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-4xl">🐾</div>
        )}
      </Link>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <div className="flex items-start gap-2">
          <Link
            to={`/app/shop/p/${product.id}?vertical=${vertical.toLowerCase()}`}
            className="min-w-0 flex-1 font-medium leading-snug hover:text-primary"
            data-testid={`shop-open-${product.id}`}
          >
            {product.name}
          </Link>
          {product.is_prescription_required && (
            <StatusPill tone="warning" data-testid={`shop-rx-${product.id}`}>Rx</StatusPill>
          )}
        </div>
        {product.brand && <p className="truncate text-xs text-muted-foreground">{product.brand}</p>}

        {product.variants.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`shop-variant-chips-${product.id}`}>
            {product.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => set_variant_id(v.id)}
                data-testid={`shop-variant-${v.id}`}
                title={
                  (v.is_purchasable ?? v.is_in_stock !== false)
                    ? undefined
                    : v.unavailable_reason === 'LISTING_PAUSED'
                      ? `${v.label} is not on sale right now`
                      : `${v.label} is out of stock`
                }
                data-active={v.id === selected.id}
                className={cn(
                  'rounded-full border-2 px-2.5 py-1 text-xs font-semibold transition-colors',
                  v.id === selected.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-brand-outline-variant/25 text-muted-foreground hover:border-primary/40',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
          <div className="min-w-0">
            <span className="font-heading text-lg font-bold tabular-nums" data-testid={`shop-price-${product.id}`}>
              ₹{selected.price_inr}
            </span>
            {selected.mrp_inr && (
              <span className="ml-1.5 text-xs text-muted-foreground line-through tabular-nums">₹{selected.mrp_inr}</span>
            )}
            {selected.discount_pct != null && (
              <span className="ml-1.5 text-xs font-semibold text-success">-{selected.discount_pct}%</span>
            )}
            {product.variants.length === 1 && <p className="text-xs text-muted-foreground">{selected.label}</p>}
            {!can_buy && (
              <p className="text-xs font-semibold text-destructive" data-testid={`shop-oos-${product.id}`}>
                {blocked_reason === 'LISTING_PAUSED'
                  ? 'Not on sale right now'
                  : blocked_reason === 'NOT_APPROVED'
                    ? 'Awaiting review'
                    : blocked_reason === 'VENDOR_UNAVAILABLE'
                      ? 'Seller unavailable'
                      : 'Out of stock'}
              </p>
            )}
          </div>

          {!can_buy ? (
            /*
             * Out of stock: keep the product on the shelf and offer to tell them.
             *
             * Hiding it would lose the sale AND the signal; this way the shopper can
             * still see the product exists, buy the same thing from another vendor, and
             * leave us a demand record for something nobody currently stocks.
             */
            <button
              type="button"
              // Nothing to promise when the blocker is review or vendor standing.
              disabled={watch_m.isPending || !watchable}
              onClick={async () => {
                try {
                  const res = await watch_m.mutateAsync({ vertical, variant_id: selected.id });
                  // It may have come back while they were looking at a stale page.
                  // `already_available` is the honest field; `already_in_stock` is its
                  // deprecated alias, read here only so an older API still works.
                  const available = res.already_available ?? res.already_in_stock;
                  set_watched((w) => ({ ...w, [selected.id]: !available }));
                } catch {
                  /* the button stays offered; tapping again retries */
                }
              }}
              data-testid={`shop-notify-${product.id}`}
              className="shrink-0 rounded-button border-2 border-brand-outline-variant/30 px-3 py-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary active:scale-95 disabled:opacity-60"
            >
              {!watchable ? (
                <>Unavailable</>
              ) : watched[selected.id] ? (
                <>
                  <BellRing className="mr-0.5 inline h-4 w-4" /> We&apos;ll tell you
                </>
              ) : (
                <>
                  <Bell className="mr-0.5 inline h-4 w-4" /> Notify me
                </>
              )}
            </button>
          ) : qty === 0 ? (
            <button
              type="button"
              onClick={add}
              data-testid={`shop-add-${product.id}`}
              className="shrink-0 rounded-button border-2 border-primary/40 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 active:scale-95"
            >
              <Plus className="mr-0.5 inline h-4 w-4" /> Add
            </button>
          ) : (
            <div className="flex shrink-0 items-center gap-2" data-testid={`shop-stepper-${product.id}`}>
              <button
                type="button"
                onClick={() => cart.set_qty(selected.id, qty - 1)}
                aria-label="Decrease"
                className="rounded-full bg-secondary p-1.5 active:scale-90"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-5 text-center font-semibold tabular-nums">{qty}</span>
              <button
                type="button"
                onClick={() => cart.inc(selected.id)}
                aria-label="Increase"
                className="rounded-full bg-primary/10 p-1.5 text-primary active:scale-90"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
